import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectionOption } from '../../entity/utility-interfaces';

@Component({
    selector: 'app-single-select-dropdown',
    templateUrl: './single-select-dropdown.component.html',
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => SingleSelectDropdownComponent),
        multi: true
      }
    ],
    standalone: false
})
export class SingleSelectDropdownComponent implements ControlValueAccessor {

  @Input() data: SelectionOption[] = [];
  @Output() selectionChange = new EventEmitter<string | undefined>();
  @Input() placeholder: string | undefined;
  @Input() className: string | undefined;
  @Input() width: string | undefined;
  @Input() height: string | undefined = '32';
  @Input() isDisabled = false;
  @Input() showClear: boolean = false;
  selectedValue: string | undefined;
  private onChange: (value: string | undefined) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  /**
   * Setter for the selection option
   */
  @Input() set selectedOption(value: any) {
    this.selectedValue = this.normalizeSelectedValue(value?.id ?? value);
  }

  constructor() {
  }

  /**
   * ControlValueAccessor hook. Called by Angular forms to push a value into the
   * component. Angular may pass `null` to clear the control, which is coerced to
   * `undefined` for internal consistency.
   * @param value The option `id` to select, or `null`/`undefined` to clear.
   */
  writeValue(value: string | null | undefined): void {
    // Angular may pass null to clear the control; coerce to undefined for internal consistency.
    this.selectedValue = value != null ? value : undefined;
  }

  /**
   * ControlValueAccessor hook. Registers the callback used to notify the form
   * model when the selected value changes.
   * @param fn Callback invoked with the selected option `id` (or `undefined`).
   */
  registerOnChange(fn: (value: string | undefined) => void): void {
    this.onChange = fn;
  }

  /**
   * ControlValueAccessor hook. Registers the callback used to mark the control
   * as touched when the user interacts with the dropdown.
   * @param fn Callback invoked on first interaction.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * ControlValueAccessor hook. Enables or disables the dropdown in response to
   * the associated form control's disabled state.
   * @param isDisabled `true` to disable the control, `false` to enable it.
   */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  /**
   * Handles the PrimeNG select change. Normalizes the value, propagates it to
   * the form model via the CVA callbacks, and emits `selectionChange` for
   * backward compatibility with existing consumers.
   */
  onClickSelectionChange() {
    this.selectedValue = this.normalizeSelectedValue(this.selectedValue);
    this.onChange(this.selectedValue);
    this.onTouched();
    this.selectionChange.next(this.selectedValue);
  }

  /**
   * Normalizes a raw value to a defined option `id` or `undefined`, so empty
   * selections consistently clear the control (enabling `Validators.required`
   * to flag the field as invalid).
   * @param value The raw value to normalize.
   * @returns The option `id` string, or `undefined` when empty.
   */
  private normalizeSelectedValue(value: string | null | undefined): string | undefined {
    return value != null ? value : undefined;
  }

}
